import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import pMemoize from 'p-memoize';
import fetchData from 'utils/fetchData';
import useError from 'hooks/useError';

const memoFetchData = pMemoize(fetchData, { maxAge: 1000 });

type Props = {
  children: ReactNode;
};

export type ProductNumber = '5' | '6' | '61'; // 6 is zaplo2, 5 is old zaplo, 61 is Google product

export type EmploymentStatus =
  | 'EMPLOYEE'
  | 'ENTERPRENEUR'
  | 'STUDENT'
  | 'SELF_EMPLOYED'
  | 'MATERNITY_LEAVE'
  | 'IN_HOUSEHOLD'
  | 'UNEMPLOYED'
  | 'RETIRED_PENSIONEER'
  | 'OTHER';

export type Education =
  | 'ELEMENTARY'
  | 'SECONDARY'
  | 'DIPLOMA'
  | 'BACHELOR'
  | 'UNIVERSITY';

export type Client = {
  id: number;
  number: string;
  status:
    | 'UNKNOWN'
    | 'REGISTERED'
    | 'IDENTIFIED'
    | 'DELETED'
    | 'VALIDATED'
    | 'ACTIVE'
    | 'NOT_IDENTIFIED';
  declaredAddress: {
    fullAddress: string;
    postalCode: string;
    location1: string;
  };
  identityDocumentDateOfExpiry: string;
  actualAddress: {
    fullAddress: string;
    postalCode: string;
    location1: string;
  };
  mobilePhoneVerified: boolean;
  firstName: string;
  lastName: string;
  fullName: string;
  personalId: string;
  identificationNumber: string;
  identityDocumentId: string;
  isDocumentIdentified: boolean;
  dateOfBirth: string;
  age: number;
  enhancements: string[];
  identifiedBy:
    | 'BANK'
    | 'BANK_ID'
    | 'MANUAL'
    | 'ID_SERVICE'
    | 'BROKER'
    | 'EMAIL'
    | 'IMMEDIATE_TRANSFER'
    | 'FINTONIC'
    | 'PRESTALO'
    | 'NOT_IDENTIFIED';
  registeredBy:
    | 'WEB'
    | 'OFFLINE'
    | 'BROKER'
    | 'CALL_CENTER'
    | 'LEAD_GENERATOR'
    | 'WEB_OTHER_PRODUCT';
  blocked: boolean;
  identified: boolean;
  bankAccount: string;
  openPrincipalAmount: number;
  homePhone: string;
  validationResolutionDetail:
    | 'PERSONAL_ID_MISSING'
    | 'EXPIRED_IDENTITY_DOCUMENT'
    | 'ERROR'
    | 'LOAN_IS_DELAYED'
    | 'CHECK_PAYSLIP'; // NOTE: This is not the full enum, instead only the values currently used on FE side
  productNumber: ProductNumber;
  additionalAmount: boolean;
  creditLimit: number;
  totalCreditLimit: number;
  hasOpenLoan: boolean;
  employer?: {
    employmentStatus?: EmploymentStatus;
    name?: string;
  };
  education?: Education;
  _embedded: {
    mobilePhone: {
      mobilePhone: string;
    };
    email: {
      email: string;
    };
  };
};

type ClientContextType = {
  client: Client | undefined;
  fetchClient: () => Promise<Client | void>;
  isClientFetching: boolean;
};

const ClientContext = createContext<ClientContextType>({} as ClientContextType);

const useClient = (): ClientContextType => useContext(ClientContext);

export function ClientProvider({ children }: Props) {
  const [client, setClient] = useState<Client | undefined>();
  const [isClientFetching, setIsClientFetching] = useState(false);
  const { showError } = useError();

  const fetchClient = useCallback(async (): Promise<Client | void> => {
    try {
      setIsClientFetching(true);
      const fetchedClient = await memoFetchData('/client');
      setClient(fetchedClient);
      return fetchedClient;
    } catch (e) {
      showError();
      throw e;
    } finally {
      setIsClientFetching(false);
    }
  }, [showError]);

  const clientContextValue = useMemo(
    () => ({
      client,
      fetchClient,
      isClientFetching,
    }),
    [client, fetchClient],
  );

  return (
    <ClientContext.Provider value={clientContextValue}>
      {children}
    </ClientContext.Provider>
  );
}

export default useClient;
